/**
 * @poc-to-production
 *
 * APS PKCE (public SPA) configuration. Client ids are not secrets — they ship
 * in the browser bundle.
 *
 * Values are read through getConfigValue, which prefers the deployed
 * environment's runtime config over the build-time `.env`. That indirection is
 * what stops a staging deploy from authenticating against production APS: the
 * pipeline ships one artifact everywhere, so the tier cannot be baked in. See
 * config/runtimeConfig.ts.
 */

import { getConfigValue } from "../config/runtimeConfig";

export type ApsTier = "prd" | "stg";

/** APS scopes — `email`/`profile` are NOT valid here; use `openid` + `user-profile:read`. */
const SCOPES = [
  "openid",
  "user-profile:read",
  "data:read",
  "data:create",
  "data:write",
  "bucket:read",
  "viewables:read",
] as const;

function tierFromEnv(): ApsTier {
  const t = (getConfigValue("VITE_APS_ENV") ?? "stg").toLowerCase();
  return t === "prd" ? "prd" : "stg";
}

function clientIdForTier(tier: ApsTier): string {
  const key =
    tier === "prd"
      ? "VITE_APS_CLIENT_ID_WEB_VERA_PRD"
      : "VITE_APS_CLIENT_ID_WEB_VERA_STG";
  const id = getConfigValue(key);
  if (!id) {
    throw new Error(
      `Missing ${key} — set it in the repo-root .env for local dev, or in ` +
        `.cloudos/service_config/ for a deployed environment`
    );
  }
  return id;
}

/** OAuth/API host — staging apps MUST NOT hit production OAuth (AUTH-001). */
export function getApsAuthBase(): string {
  const override = getConfigValue("VITE_APS_AUTH_BASE");
  if (override) return override.replace(/\/$/, "");
  return tierFromEnv() === "prd"
    ? "https://developer.api.autodesk.com"
    : "https://developer-stg.api.autodesk.com";
}

export function getApsTier(): ApsTier {
  return tierFromEnv();
}

export function getApsClientId(): string {
  return clientIdForTier(tierFromEnv());
}

/** First 8 chars of the active PKCE client id — safe to show in UI for provisioning checks. */
export function getApsClientIdPreview(): string {
  const id = getApsClientId();
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

export function getApsScopes(): string {
  return SCOPES.join(" ");
}

/** Must match APS app callback URLs exactly (origin + /callback). */
export function getRedirectUri(): string {
  return `${window.location.origin}/callback`;
}

export function getAuthorizeUrl(): string {
  return `${getApsAuthBase()}/authentication/v2/authorize`;
}

export function getTokenUrl(): string {
  return `${getApsAuthBase()}/authentication/v2/token`;
}

/**
 * Skip APS PKCE login during local development unless opted in.
 *
 * - Production builds: auth on (unless VITE_DISABLE_AUTH=true).
 * - `pnpm dev`: auth off by default (fast offline demos). Set
 *   VITE_ENABLE_APS_AUTH=true in repo-root `.env` for real login + ACC APIs.
 */
export function isAuthDisabled(): boolean {
  if (getConfigValue("VITE_DISABLE_AUTH") === "true") return true;
  if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_APS_AUTH !== "true") {
    return true;
  }
  return false;
}
